import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getWallet, shortAddr } from "@/lib/wallet";
import Logo from "@/components/Logo";
import { CircleUser, Menu, X } from "lucide-react";

const NAV = [
  { to: "/markets/evm",    label: "EVM"         },
  { to: "/markets/solana", label: "Solana"      },
  { to: "/leaderboard",    label: "Leaderboard" },
  { to: "/#faq",           label: "FAQ",         hash: "faq" },
  { to: "/referrals",      label: "Refer"       },
];

export default function Navbar({ accent = "white" }) {
  const [wallet, setWallet] = useState(null);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const sync = () => setWallet(getWallet());
    sync();
    window.addEventListener("stk-wallet-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("stk-wallet-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, [loc.pathname]);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Lock body scroll when menu open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else      document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const accentClass = !wallet
    ? (accent === "evm" ? "grad-evm-bg btn-glow-evm" : accent === "solana" ? "grad-sol-bg btn-glow-sol" : "")
    : "";
  const accentText = accent === "white" || !accent ? "#000" : "#fff";

  return (
    <header
      className="sticky top-0 z-50 border-b backdrop-blur-xl"
      style={{ background: "rgba(5,5,5,0.7)", borderColor: "var(--border)" }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[60px] sm:h-[64px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-home-link">
          <Logo size={28} />
          <span className="font-display font-black text-base sm:text-lg tracking-tighter">STAKEFORGE</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-sm">
          {NAV.map(n => {
            const active = !n.hash && loc.pathname === n.to;
            const handleHashClick = (e) => {
              if (!n.hash) return;
              e.preventDefault();
              if (loc.pathname === "/") {
                document.getElementById(n.hash)?.scrollIntoView({ behavior: "smooth" });
              } else {
                nav(n.to);
              }
            };
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={handleHashClick}
                data-testid={`nav-${n.label.toLowerCase()}-link`}
                className="px-3 py-1.5 transition-colors"
                style={{
                  color: active ? "#fff" : "var(--text-2)",
                  borderBottom: active ? "1px solid #fff" : "1px solid transparent",
                }}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (wallet) {
                // Wallet connected → React profile (history, stats, balances)
                nav("/profile");
              } else {
                // Not connected → HTML portfolio page with two wallet-connect cards
                window.location.href = "portfolio/index.html";
              }
            }}
            data-testid="nav-profile-btn"
            className={`px-3 sm:px-4 h-9 inline-flex items-center gap-2 font-bold text-xs sm:text-sm tracking-wide ${accentClass}`}
            style={{
              background: wallet ? "transparent" : (accent === "white" || !accent ? "#fff" : undefined),
              color: wallet ? "#fff" : accentText,
              border: wallet ? "1px solid var(--border-strong)" : "0",
            }}
          >
            {wallet ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: "var(--success)" }} />
                <span className="font-mono">{shortAddr(wallet.address)}</span>
              </>
            ) : (
              <>
                <CircleUser size={14} strokeWidth={2.5} />
                <span className="hidden sm:inline">PROFILE</span>
              </>
            )}
          </button>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-menu-toggle"
            aria-label="Open menu"
            className="md:hidden w-9 h-9 grid place-items-center border"
            style={{ borderColor: "var(--border-strong)", color: "#fff" }}
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div
          className="md:hidden absolute left-0 right-0 top-[60px] border-b fade-in"
          style={{ background: "rgba(5,5,5,0.98)", borderColor: "var(--border)" }}
          data-testid="nav-menu-drawer"
        >
          <nav className="flex flex-col">
            {NAV.map((n, i) => {
              const active = !n.hash && loc.pathname === n.to;
              const handleClick = (e) => {
                if (!n.hash) return;
                if (loc.pathname === "/") {
                  e.preventDefault();
                  setOpen(false);
                  setTimeout(() => document.getElementById(n.hash)?.scrollIntoView({ behavior: "smooth" }), 80);
                }
              };
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={handleClick}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}-link`}
                  className="px-5 py-4 border-b text-sm uppercase tracking-[0.2em] font-bold flex items-center justify-between fade-up stagger"
                  style={{
                    borderColor: "var(--border)",
                    color: active ? "#fff" : "var(--text-2)",
                    "--i": i,
                  }}
                >
                  <span>{n.label}</span>
                  <span style={{ color: active ? "#fff" : "var(--text-3)" }}>→</span>
                </Link>
              );
            })}
            <Link
              to="/legal"
              className="px-5 py-3 text-[11px] uppercase tracking-[0.25em]"
              style={{ color: "var(--text-3)" }}
              data-testid="nav-mobile-legal-link"
            >
              Legal
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
